import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { JobImageSpendView, JobPostDesign, JobPostDesignView, JobPostPromptPreview, PreviewJobPostPromptInput } from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost, apiPut, errorMessage, request } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { jobKey } from './jobs-api';

// ADR 0066 — the Job post section's design: colours, fonts, picture, own instructions and pictures, Show instructions
// and this month's spend. Under the job's key, so the realtime hint (`['jobs']`) refreshes it.

export function jobPostDesignKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'post-design'];
}

export function jobImageSpendKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'image-spend'];
}

/** Where an own picture's thumbnail opens. The API checks the job's permission. */
export function postReferenceUrl(jobId: string, documentId: string): string {
  return `/api/v1/jobs/${jobId}/post-references/${documentId}/file`;
}

export function useJobPostDesign(jobId: string): UseQueryResult<JobPostDesignView, ApiError> {
  return useQuery<JobPostDesignView, ApiError>({
    queryKey: jobPostDesignKey(jobId),
    queryFn: () => apiGet<JobPostDesignView>(`/jobs/${jobId}/post-design`),
    placeholderData: (previous) => previous,
  });
}

export function useJobImageSpend(jobId: string): UseQueryResult<JobImageSpendView, ApiError> {
  return useQuery<JobImageSpendView, ApiError>({
    queryKey: jobImageSpendKey(jobId),
    queryFn: () => apiGet<JobImageSpendView>(`/jobs/${jobId}/image-spend`),
    placeholderData: (previous) => previous,
  });
}

interface DesignActions {
  readonly save: (design: JobPostDesign) => void;
  readonly reset: () => void;
  readonly saveAsVentureDefault: () => void;
  readonly addPicture: (file: File) => void;
  readonly removePicture: (documentId: string) => void;
  readonly isAddingPicture: boolean;
}

/** Every change applies on screen at once and rolls back with the reason if the server refuses (§5a rule 1). */
export function useJobPostDesignActions(jobId: string): DesignActions {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const key = jobPostDesignKey(jobId);
  type Context = { readonly before: JobPostDesignView | undefined };
  const optimistic = async (change: (view: JobPostDesignView) => JobPostDesignView): Promise<Context> => {
    await queryClient.cancelQueries({ queryKey: key });
    const before = queryClient.getQueryData<JobPostDesignView>(key);
    if (before) queryClient.setQueryData<JobPostDesignView>(key, change(before));
    return { before };
  };
  const failed = (message: string) => (error: ApiError, _input: unknown, context: Context | undefined): void => {
    queryClient.setQueryData(key, context?.before);
    show({ tone: 'danger', message, detail: errorMessage(error) });
  };
  const settle = (view: JobPostDesignView): void => {
    queryClient.setQueryData(key, view);
  };

  const save = useMutation<JobPostDesignView, ApiError, JobPostDesign, Context>({
    mutationFn: (design) => apiPut<JobPostDesignView>(`/jobs/${jobId}/post-design`, { design }),
    onMutate: (design) => optimistic((view) => ({ ...view, design })),
    onSuccess: settle,
    onError: failed("Couldn't save the design"),
  });
  const reset = useMutation<JobPostDesignView, ApiError, void, Context>({
    mutationFn: () => apiDelete<JobPostDesignView>(`/jobs/${jobId}/post-design`),
    onMutate: () => optimistic((view) => ({ ...view, design: { ...view.design, ...view.ventureDefault } })),
    onSuccess: settle,
    onError: failed("Couldn't reset the design"),
  });
  const ventureDefault = useMutation<JobPostDesignView, ApiError, void, Context>({
    mutationFn: () => apiPost<JobPostDesignView>(`/jobs/${jobId}/post-design/venture-default`, {}),
    onMutate: () =>
      optimistic((view) => ({
        ...view,
        isVentureDefaultSaved: true,
        ventureDefault: { palette: view.design.palette, headlineFont: view.design.headlineFont, bodyFont: view.design.bodyFont },
      })),
    onSuccess: (view) => {
      settle(view);
      show({ tone: 'success', message: 'Saved to the brand kit', detail: 'New posts for this venture start with these colours and fonts.' });
    },
    onError: failed("Couldn't save to the brand kit"),
  });
  const add = useMutation<JobPostDesignView, ApiError, File>({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append('file', file);
      return (await request<JobPostDesignView>(`/jobs/${jobId}/post-references`, { method: 'POST', body })).data;
    },
    onSuccess: settle,
    onError: (error, file) => show({ tone: 'danger', message: `${file.name} wasn't added`, detail: errorMessage(error) }),
  });
  const remove = useMutation<JobPostDesignView, ApiError, string, Context>({
    mutationFn: (documentId) => apiDelete<JobPostDesignView>(`/jobs/${jobId}/post-references/${documentId}`),
    onMutate: (documentId) =>
      optimistic((view) => ({ ...view, design: { ...view.design, references: view.design.references.filter((reference) => reference.documentId !== documentId) } })),
    onSuccess: settle,
    onError: failed("Couldn't remove the picture"),
  });
  return {
    save: (design) => save.mutate(design),
    reset: () => reset.mutate(),
    saveAsVentureDefault: () => ventureDefault.mutate(),
    addPicture: (file) => add.mutate(file),
    removePicture: (documentId) => remove.mutate(documentId),
    isAddingPicture: add.isPending,
  };
}

/** Show instructions: free; the words and design on screen go in, the exact text comes back. */
export function useJobPostPrompt(jobId: string): {
  readonly preview: (input: PreviewJobPostPromptInput) => void;
  readonly result: JobPostPromptPreview | null;
  readonly isLoading: boolean;
  readonly clear: () => void;
} {
  const show = useToasts((toasts) => toasts.show);
  const mutation = useMutation<JobPostPromptPreview, ApiError, PreviewJobPostPromptInput>({
    mutationFn: (input) => apiPost<JobPostPromptPreview>(`/jobs/${jobId}/post-prompt`, input),
    onError: (error) => show({ tone: 'danger', message: "Couldn't show the instructions", detail: errorMessage(error) }),
  });
  return { preview: (input) => mutation.mutate(input), result: mutation.data ?? null, isLoading: mutation.isPending, clear: () => mutation.reset() };
}
